import { group, topic } from "./schema.js";

export const SELLER_META = {
  role: "seller",
  title: "Seller Manual",
  kicker: "An honest brief for the home you already love",
  whyHeading: "Why This Helps You and Your Agent",
};

export const SELLER_TREE = [
  topic({
    slug: "start-here",
    title: "Start Here",
    terms: ["getting started", "listing brief", "overview"],
    forYou:
      "This book is for sellers using REChecklist. It is a listing brief, not a public ad, and not a video — that is the Guided Tour. Buyer answers never mix with these pages.",
    how: [
      "Sign in as a seller so your agent can match this brief to you.",
      "Walk Welcome through Property, Interior, Exterior, Upgrades, Condition, Photos, Improvements, and Summary.",
      "Add notes for anything a photo cannot say — the leak you already fixed, the floor that sings, the view that is seasonal.",
      "Upload photos when the truth is already written. Condition before pretty pictures is a kindness.",
      "Message your agent when timing, paint, or price tone needs a person, not a checkbox.",
    ],
    why:
      "Sellers often describe a feeling (“we’ve loved this kitchen,” “the yard is special,” “it’s mostly updated”) that is hard to list with honesty. This journey turns those instincts into a usable brief — features, condition, photos, and notes — so your agent can represent what is true. Honest homes sell with less drama and fewer surprises later.",
    examples:
      "“Kitchen 2021, baths original” plus a daylight photo and a note if a wall came down is a brief. “Updated” is a hope your agent cannot defend in a showing.",
    tips: [
      "You do not have to finish photos on day one.",
      "If a word is unfamiliar, search this book. That is what it is for.",
    ],
    related: ["condition", "photos", "notes"],
    href: "/seller/home",
  }),

  group("Getting around", [
    topic({
      slug: "navigation",
      title: "Menus and pages",
      terms: ["menu", "seller journey", "header"],
      forYou:
        "The top menu is honest about two journeys. Stay on Seller Journey while you are describing this home.",
      how: [
        "Use Seller Home or Seller Journey for the working path.",
        "Use the step list on seller pages — Welcome, Property, Interior, and so on — as your map.",
        "Use Information & Resources when you need the office card at the bottom of any page.",
      ],
      why:
        "Two carpets, never mixed. If the rooms stay in the same place, you spend your energy on the truth of the house, not on the software.",
      examples:
        "Buyer Journey will not copy your photos. Click it only if you mean to switch roles entirely.",
      tips: ["Main Dashboard is a pictured porch for both journeys. Seller Journey on that grid is your working card."],
      related: ["seller-home", "information-resources"],
      href: "/seller/home",
    }),
    topic({
      slug: "seller-home",
      title: "Seller Home",
      terms: ["dashboard", "seller landing"],
      forYou:
        "Seller Home is the door to the listing brief — a calm landing, not a marketplace feed.",
      how: [
        "Begin here, then walk Welcome when you are ready.",
        "Return whenever you need a breath. It will not erase what you have written.",
      ],
      why:
        "First impressions should feel like a porch light. A brief you can find is a brief you will finish.",
      examples:
        "Show a spouse Seller Home, then continue to Condition together. That is allowed.",
      tips: ["The companion sidebar — Notes, Tips, Readiness — belongs to every seller page, not only Home."],
      related: ["welcome", "notes", "start-here"],
      href: "/seller/home",
    }),
    topic({
      slug: "information-resources",
      title: "Information & Resources",
      terms: ["footer", "office", "privacy"],
      forYou:
        "The office card, privacy, terms, and notices live in the footer of every page.",
      how: [
        "Use the top-menu link or scroll to the bottom.",
        "Read the agent or broker lines that fill from their profiles.",
      ],
      why:
        "You should always know whom you are working with. License numbers are recorded by the office — not certified by this product as a DRE credential.",
      examples:
        "If the card looks empty, ask your agent to save Profile. That question is welcome.",
      tips: ["Conversation stays in Messages. Notices are system updates."],
      related: ["navigation", "messaging"],
    }),
  ]),

  group("Seller Journey", [
    topic({
      slug: "welcome",
      title: "Welcome",
      terms: ["first page", "seller welcome"],
      forYou:
        "Welcome is a greeting for the home you already know. You are not taking a test. You are telling the truth at a human pace.",
      how: [
        "Read the welcome.",
        "Continue to Property Details when you are ready.",
        "Notes can begin on day one.",
      ],
      why:
        "Beginnings set tone. An honest brief starts with permission to go slowly.",
      examples:
        "Ten minutes: Welcome and Property. Condition later. Photos after the painter. That is allowed.",
      tips: ["Buyer Start is a different house. Stay here."],
      related: ["property-details", "notes"],
      href: "/seller/welcome",
    }),
    topic({
      slug: "property-details",
      title: "Property details",
      terms: ["address", "beds", "baths", "year", "size"],
      forYou:
        "Property details are the facts of the home — beds, baths, year, size — so your agent is not guessing from memory.",
      how: [
        "Fill what you know.",
        "Use Notes for anything a field cannot hold.",
        "Be kind to future-you. Facts you skip become emails later.",
      ],
      why:
        "Your agent can represent a number they can see. They cannot represent a number that lives only in a drawer.",
      examples:
        "Year built plus a note that a room was permitted in 2018 is a complete sentence. Year built alone is a start.",
      tips: ["Don’t forget Notes on the left for this page."],
      related: ["interior-features", "condition", "notes"],
      href: "/seller/property-details",
    }),
    topic({
      slug: "interior-features",
      title: "Interior features",
      terms: ["kitchen", "baths", "rooms", "layout"],
      forYou:
        "Interior features describe the inside that is actually there — including the guest bath you might paint before photos.",
      how: [
        "Mark what is included.",
        "Notes for the year of a kitchen refresh, or a wall that came down.",
        "Photos later, after the truth is written.",
      ],
      why:
        "Buyers and agents both start in the kitchen. The brief should too — with honesty, not with a magazine hope.",
      examples:
        "“Guest bath original; we may paint before photos” belongs here, in Improvements, and in Messages if you want advice.",
      tips: ["Don’t forget Notes on the left — you will use that habit again on Exterior."],
      related: ["photos", "improvements", "upgrades"],
      href: "/seller/interior-features",
    }),
    topic({
      slug: "exterior-features",
      title: "Exterior features",
      terms: ["yard", "roof", "pool", "fence", "outdoor"],
      forYou:
        "Exterior features tell your agent what the yard and envelope include — including the fence the neighbor shares.",
      how: [
        "On the Exterior features page, use checkboxes and buttons so your agent knows what is included with your home.",
        "Note shared fences, seasonal views, and the tree that photographs well.",
        "Don’t forget to add any notes for this page in the Notes section on the left sidebar that pertains to this page.",
      ],
      why:
        "Outdoor truth is part of listing readiness, not an afterthought. Your agent will see everything, and that is how they represent you with clarity.",
      examples:
        "Pool: no. Fence: shared on the north. A note about the oak in full leaf. That is a yard brief.",
      tips: ["Photos of the yard in the season you have, plus a note about the season you do not, is honesty."],
      related: ["photos", "condition", "notes"],
      href: "/seller/exterior-features",
    }),
    topic({
      slug: "upgrades",
      title: "Upgrades",
      terms: ["updates", "remodel", "new", "kitchen year"],
      forYou:
        "Upgrades answer “what’s new?” — the work you have already done, dated as best you can.",
      how: [
        "List what changed and when.",
        "Add Notes if a wall came down or a permit matters.",
        "Leave pretty pictures for the Photos page.",
      ],
      why:
        "Buyers and agents both ask this question. A dated sentence is kinder than “mostly updated.”",
      examples:
        "“Kitchen 2021, baths original” is enough to start. Add Notes if the layout changed.",
      tips: ["Condition is the cousin of this page. Finish both before you fuss over filters on Photos."],
      related: ["condition", "interior-features", "improvements"],
      href: "/seller/upgrades",
    }),
    topic({
      slug: "condition",
      title: "Condition",
      terms: ["roof year", "hvac", "honesty", "disclose", "exceptions"],
      forYou:
        "Condition is the honesty stack: what is true about systems, wear, and the things a photo cannot say.",
      how: [
        "Mark roof year, HVAC, and what you know.",
        "Write the floor that sings, the leak you already fixed, the warranty that remains.",
        "Calm sentences are enough. Honesty is not the same as alarm.",
      ],
      why:
        "Your agent cannot represent a surprise they were never told. Surprises found later cost more than a calm sentence today.",
      examples:
        "After you mark a roof year, add: “Replaced 2019, still under workmanship warranty.” Don’t forget Notes on the left each time something is personal or historical.",
      tips: ["Condition first, photos second, is still the kind order."],
      related: ["photos", "notes", "how-agents-use-this"],
      href: "/seller/condition-notes",
    }),
    topic({
      slug: "photos",
      title: "Photos",
      terms: ["upload", "captions", "camera", "gallery"],
      forYou:
        "Photos show what is there — in the light you have — with captions for what a picture cannot say. This is not a public gallery.",
      how: [
        "Upload after Condition feels true.",
        "Caption the tree, the kitchen, the view.",
        "Keep files kind: photos compress on this device; huge camera originals may need a smaller export.",
      ],
      why:
        "A listing brief without pictures is a silhouette. Pictures without captions hide the season, the shared fence, the room that is original. Together they are something your agent can use.",
      examples:
        "Caption: “Oak in full leaf, west yard.” Notes: “Bare in February; still private.” That is enough.",
      tips: [
        "If a save fails, try one photo at a time.",
        "Don’t forget Notes on the left for the story behind a picture.",
      ],
      related: ["condition", "exterior-features", "file-packet"],
      href: "/seller/photos",
    }),
    topic({
      slug: "improvements",
      title: "Improvements",
      terms: ["paint", "before photos", "to-do", "work"],
      forYou:
        "Improvements are the work you might still do — paint, small repairs — so timing and photos stay honest.",
      how: [
        "Name what you might change, and what you will leave.",
        "Message your agent if you want advice on timing.",
        "Keep Readiness in the sidebar for listing-day thinking, not as a quiz.",
      ],
      why:
        "A pretty photo of a bath you plan to change is a kindness to schedule. Your agent can time painters, photos, and showings when they know the plan.",
      examples:
        "“Should we paint the guest bath before photos?” belongs in Messages, with a note on this page too.",
      tips: ["Don’t forget Notes on the left — you already practiced that on Exterior."],
      related: ["photos", "messaging", "listing-readiness"],
      href: "/seller/improvements",
    }),
    topic({
      slug: "listing-readiness",
      title: "Listing readiness",
      terms: ["readiness", "listing day", "sidebar"],
      forYou:
        "Readiness lives in the companion sidebar. It is thinking about listing day — not a scoreboard.",
      how: [
        "Open the Readiness tab when you are ready to think about timing.",
        "Use Tips for short nudges. Use Notes for the story.",
      ],
      why:
        "Listing day should not be a surprise you invent alone at 10 p.m. A sidebar that holds the question keeps the brief calm.",
      examples:
        "Yard photo-ready is a readiness thought. Perfect is not required. True is.",
      tips: ["Readiness is optional on day one. Condition is not."],
      related: ["improvements", "photos", "tips"],
    }),
    topic({
      slug: "portfolio-summary",
      title: "Portfolio summary",
      terms: ["summary", "review", "done"],
      forYou:
        "Summary gathers the brief so you and your agent can see the home in one place — features, condition, photos, notes.",
      how: [
        "Review. Return to any page that still feels thin.",
        "Read Notes once more.",
        "When it feels true, you have given your agent something usable.",
      ],
      why:
        "Professionals notice when a seller finishes with care. A complete brief means a listing that can be defended in a showing.",
      examples:
        "If photos are empty and Condition is rich, add pictures when you can. Summary will wait.",
      tips: ["This is a pause, not a goodbye. You can still update."],
      related: ["photos", "condition", "file-packet"],
      href: "/seller/portfolio-summary",
    }),
  ]),

  group("Companion tools", [
    topic({
      slug: "notes",
      title: "Notes",
      terms: ["honesty", "exceptions", "sidebar"],
      forYou:
        "Notes say what a listing photo cannot. They stay on the seller journey and never spill into a buyer checklist.",
      how: [
        "Use the Notes tab on any page.",
        "Be specific. Kind and true ages well in a file.",
        "Repeat the habit whenever something is personal or historical.",
      ],
      why:
        "Your agent cannot represent a surprise they were never told. Notes are how you give them the gift of the truth.",
      examples:
        "On Condition, after you mark a roof year, add the warranty sentence. Don’t forget Notes on the left each time. Your agent will see everything.",
      tips: ["Honesty is not alarm. Calm sentences are enough."],
      related: ["condition", "photos", "how-agents-use-this"],
    }),
    topic({
      slug: "tips",
      title: "Tips",
      terms: ["reminders", "sidebar"],
      forYou:
        "Tips are a gentle coach beside the page — the pocket version of this book.",
      how: [
        "Open Tips for a short nudge.",
        "Return to Notes or Readiness when you are working.",
      ],
      why:
        "Some people learn by doing. Tips keep the doing kind.",
      examples:
        "A tip might remind you that condition belongs before photos. You have already read that here.",
      tips: ["Optional. The brief works without them. They are there so you never feel stuck."],
      related: ["notes", "listing-readiness"],
    }),
  ]),

  group("Staying in touch", [
    topic({
      slug: "messaging",
      title: "Messaging",
      terms: ["messages", "ask", "rapport"],
      forYou:
        "Messages is the room for timing, paint, photos, and price tone — a quiet thread with your agent, not a public inbox.",
      how: [
        "Open Messages once you are signed in.",
        "Ask one clear question. Attach a photo if it helps.",
        "Write as if a kind broker might read it later.",
      ],
      why:
        "Rapport lives in complete sentences. A question about the guest bath is how professionals work together — not a failure.",
      examples:
        "“Painter comes Thursday; photos cannot be Friday morning.” That is a calendar sentence and a Message.",
      tips: ["If you ever feel unsure, the sidebar search in this book can guide you instantly — then ask your agent the rest."],
      related: ["improvements", "photos", "calendar"],
      href: "/client/messages",
    }),
    topic({
      slug: "calendar",
      title: "Calendar",
      terms: ["photos day", "painter", "showings"],
      forYou:
        "Dates live on your agent’s desk, with the file. You request. They record.",
      how: [
        "Send timing in Messages.",
        "Confirm when photos, painters, or first showings are set.",
      ],
      why:
        "A date that lives only in a text thread is how Thursday gets tangled.",
      examples:
        "“Painter comes Thursday; photos cannot be Friday morning.” Your agent will place that on the desk calendar.",
      tips: ["Community events on this site are separate from file dates."],
      related: ["messaging", "photos"],
    }),
  ]),

  group("Your file", [
    topic({
      slug: "file-packet",
      title: "File Packet",
      terms: ["packet", "seller file"],
      forYou:
        "Your agent assembles the File Packet. You contribute features, condition, photos, and notes.",
      how: [
        "Complete the journey pages.",
        "Your agent opens Files to see the brief together.",
        "Ask them to walk you through it once if you like.",
      ],
      why:
        "Paper planners scatter. A packet holds the home in one place so listing day is not a scavenger hunt.",
      examples:
        "Before a listing appointment, a good agent can skim Condition and Photos without shuffling a camera roll.",
      tips: ["The original lives with the office. You do not need a second copy."],
      related: ["photos", "how-agents-use-this"],
    }),
    topic({
      slug: "how-agents-use-this",
      title: "How your agent uses what you add",
      terms: ["listing", "showings", "honesty"],
      forYou:
        "Every checkbox, photo, and note is a sentence your agent will use to represent the home with clarity.",
      how: [
        "They read Condition with Photos, not instead of Photos.",
        "They time painters and pictures from Improvements and Messages.",
        "They answer buyer questions from the brief — not from a guess.",
      ],
      why:
        "Honest homes sell with less drama. Your agent can defend what is true. They cannot defend what was never written.",
      examples:
        "A 2019 roof with a warranty note is already in the packet before anyone talks price tone. That is the gift.",
      tips: ["The more true the Notes, the kinder the showings."],
      related: ["condition", "photos", "notes"],
    }),
  ]),
];
